import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/* =====================================================================
   Shared showcase primitives
   - Section shell (bg + glow + header)
   - TilesGallery / DeviceStrip  (reduced-motion + mobile fallbacks)
   - PhoneFrame bezel
   - helpers: useMediaQuery, clamp, lerp, HOUSE_EASE, reveal
   Each signature animation (Haiku/Thrive/Conduit/Soap/Sterling) composes
   these and mounts its heavy scroll tree ONLY on desktop + motion-allowed.
   ===================================================================== */

export const HOUSE_EASE = [0.22, 1, 0.36, 1];

export const clamp = (min, max, v) => Math.min(max, Math.max(min, v));
export const lerp = (a, b, t) => a + (b - a) * t;

// matchMedia hook - drives whether the heavy animated tree is even mounted.
export function useMediaQuery(query) {
  const [match, setMatch] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setMatch(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return match;
}

export const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: HOUSE_EASE, delay: i * 0.07 },
  }),
};

/* ----------------------------- Section shell ----------------------------- */

export const Section = styled.section`
  position: relative;
  /* NEVER set overflow/transform/filter/will-change here: this is an ancestor
     of the pinned (position:sticky) and 3D (preserve-3d) stages and any of
     those would break them. Clipping lives on the inner Stage instead. */
  padding: 96px 0;
  background: ${(p) => p.$bg || 'transparent'};
  ${(p) => p.$borderTop && `border-top: 1px solid ${p.$borderTop};`}
  ${(p) => p.$borderBottom && `border-bottom: 1px solid ${p.$borderBottom};`}

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

export const Glow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: ${(p) => p.$glow};
`;

export const Inner = styled.div`
  position: relative;
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

const HeaderWrap = styled.div`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2.25rem;
  }
`;

const Eyebrow = styled(motion.div)`
  font-family: ${(p) => p.$eyebrowFont || 'inherit'};
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${(p) => p.$accent};
  margin-bottom: 1rem;
`;

const Title = styled(motion.h2)`
  font-family: ${(p) => p.$titleFont};
  font-weight: 600;
  color: ${(p) => p.$ink};
  line-height: 1.05;
  letter-spacing: -0.02em;
  font-size: clamp(2rem, 4vw, 3rem);
  margin: 0 0 0.85rem;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.08rem;
  line-height: 1.6;
  color: ${(p) => p.$muted};
  margin: 0 auto;
  max-width: 560px;
`;

export function ShowcaseHeader({ eyebrow, title, subtitle, theme }) {
  const vp = { once: true, amount: 0.4 };
  return (
    <HeaderWrap>
      {eyebrow && (
        <Eyebrow
          $accent={theme.accent}
          $eyebrowFont={theme.eyebrowFont}
          initial="hidden"
          whileInView="show"
          viewport={vp}
          variants={reveal}
        >
          {eyebrow}
        </Eyebrow>
      )}
      {title && (
        <Title
          $titleFont={theme.titleFont}
          $ink={theme.ink}
          initial="hidden"
          whileInView="show"
          viewport={vp}
          variants={reveal}
          custom={1}
        >
          {title}
        </Title>
      )}
      {subtitle && (
        <Subtitle
          $muted={theme.muted}
          initial="hidden"
          whileInView="show"
          viewport={vp}
          variants={reveal}
          custom={2}
        >
          {subtitle}
        </Subtitle>
      )}
    </HeaderWrap>
  );
}

export const Hint = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${(p) => p.$muted};
`;

/* ----------------------------- Phone frame ------------------------------- */

export const PhoneFrame = styled.div`
  position: relative;
  border-radius: 40px;
  padding: 9px;
  background: ${(p) => p.$bezel};
  box-shadow: 0 22px 50px rgba(20, 16, 10, 0.26), 0 6px 16px rgba(20, 16, 10, 0.12),
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.06);

  & > .screen {
    position: relative;
    border-radius: 32px;
    overflow: hidden;
    aspect-ratio: 1320 / 2868;
    background: #fff;
  }
  & > .screen img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;


/* small util: measure an element's size (live) */
export function useElementWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}
